import React, { Dispatch, SetStateAction, useState } from 'react';
import {
	FieldErrors,
	FieldValues,
	UseFormRegister,
	UseFormSetValue,
	UseFormTrigger,
	UseFormWatch,
} from 'react-hook-form';

import { Button, Checkbox, Inform, Input, Modal, SliderCheckbox, Tooltip } from '@components/common';

import { TNameTitle } from '@interfaces/common/common';
import { IExtendsCategory } from '@interfaces/organisation/organisations';

import { TUserRoles } from '@enums/common';

import { MODAL_WIDTH_480 } from '@constants/modals';
import { regExpWithoutSpace } from '@constants/regExp';
import {
	fieldsToTestOrganisationType,
	organisationTelecomIdTooltip,
	tabsNames,
} from '../constants';
import { warningInformText, warningInformTextBody } from './constants';

import styles from './OrganisationType.module.scss';

export interface IOrganisationTypeProps {
	setCurrentTab: React.Dispatch<React.SetStateAction<string>>;
	isEditMode: boolean;
	organisationCategories: Array<IExtendsCategory>;
	blockTooltipText: string;
	register: UseFormRegister<FieldValues>;
	errors: FieldErrors;
	setValue: UseFormSetValue<FieldValues>;
	isMNO: boolean;
	setIsDirtyFormModal: React.Dispatch<React.SetStateAction<boolean>>;
	trigger: UseFormTrigger<FieldValues>;
	watch: UseFormWatch<FieldValues>;
	isWarningUncheckTypesModalOpened: boolean;
	setIsWarningUncheckTypesModalOpened: Dispatch<SetStateAction<boolean>>;
	setIsRevokedRolesModal: Dispatch<SetStateAction<boolean>>;
	prevTypes?: Array<string>;
	revokedRoles?: TUserRoles[];
	organisationTypes: Array<TNameTitle>;
	isOrganisationTypeTabError: boolean;
}

const OrganisationType = ({
	blockTooltipText,
	errors,
	isEditMode,
	organisationCategories,
	organisationTypes,
	register,
	setCurrentTab,
	setValue,
	isMNO,
	setIsDirtyFormModal,
	trigger,
	watch,
	isWarningUncheckTypesModalOpened,
	setIsWarningUncheckTypesModalOpened,
	prevTypes,
	revokedRoles,
	setIsRevokedRolesModal,
	isOrganisationTypeTabError,
}: IOrganisationTypeProps) => {
	const [isOpenWarningModal, setIsOpenWarningModal] = useState(false);

	const categoryIds = watch('categoryIds');
	const types = watch('types');

	const onClickBackHandler = () => setCurrentTab(tabsNames.address);

	const switchToNext = async () => {
		const isValid = await trigger(fieldsToTestOrganisationType);

		if (!isValid) return;

		if (!isEditMode) {
			setCurrentTab(tabsNames.roleAvailability);
		}
	};

	const closeWarningModal = () => setIsOpenWarningModal(false);

	const onCategoryChange = (value: string, checkedCategory: boolean) => () => {
		setValue('categoryIds',
			checkedCategory
				? categoryIds.filter((id: string) => id !== value)
				: [...categoryIds, value]);

		setIsDirtyFormModal(true);
	};

	const sortedOrganisationCategory = organisationCategories.sort((a, b) => {
		return a.label === 'Other'
			? 1
			: b.label === 'Other' ? -1 : a.label.localeCompare(b.label);
	});

	return (
		<>
			<div className={styles.tabContainer}>
				<Modal
					isOpen={isOpenWarningModal}
					onClose={closeWarningModal}
					title='Warning'
					width={MODAL_WIDTH_480}
				>
					<Inform
						informText={warningInformText}
						onClose={closeWarningModal}
					>
						{warningInformTextBody}
					</Inform>
				</Modal>
				<div className={styles.title}>
					Select organisation types<span>*</span>
				</div>
				<div className={styles.organisationTypeSection}>
					<div className={styles.organisationTypeContainer}>
						{organisationTypes.map(organisationType => {
							const organisationTypeName = organisationType.name;
							const checkedOrganisationType = types.includes(organisationTypeName);

							const onChangeOrganisationType = (id: string, checked: boolean) => {
								setValue('types',
									checked
										? [...types, organisationTypeName]
										: types.filter((type: string) => type !== id),
								);

								const isNeedToOpenWarning = !isWarningUncheckTypesModalOpened
									&& prevTypes?.includes(organisationTypeName);

								if (!checked) {
									if (isNeedToOpenWarning) {
										setIsOpenWarningModal(true);
										setIsWarningUncheckTypesModalOpened(true);
									}
									if (types?.length === 1) {
										setValue('roleAvailability', []);
									}
								} else if (revokedRoles?.length && !prevTypes?.includes(id)) {
									setIsRevokedRolesModal(true);
								}

								setIsDirtyFormModal(true);
							};

							return (
								<div
									key={organisationType.name}
									className={styles.organisationTypeCheckbox}
								>
									<SliderCheckbox
										checked={checkedOrganisationType}
										id={organisationType.name}
										isBold
										isLabelAfter
										label={organisationType.title}
										onChange={onChangeOrganisationType}
									/>
								</div>
							);
						})}
					</div>
					<div className={styles.inputsWrapper}>
						<div className={styles.organisationTelecomId}>
							<Input
								disabled={!isMNO}
								errorMessage={errors?.organizationTelecomId?.message}
								label='Organisation Telecom ID'
								name='organizationTelecomId'
								placeholder='Create Telecom ID'
								regexValidation={regExpWithoutSpace}
								register={register}
								setValue={setValue}
								tooltipContent={organisationTelecomIdTooltip}
								tooltipPlacement='auto'
							/>
						</div>
					</div>
				</div>
				<div className={styles.title}>
					Select organisation categories
				</div>
				<div className={styles.organisationCategoriesContainer}>
					<div className={styles.column}>
						{sortedOrganisationCategory.filter((category, index) => {
							return index < Math.ceil(sortedOrganisationCategory.length / 2);
						}).map(({ label, value }) => {
							const checkedCategory = categoryIds.includes(value);

							return (
								<div
									key={value}
									className={styles.categoryItem}
								>
									<Checkbox
										checked={checkedCategory}
										label={label}
										name={value}
										onChange={onCategoryChange(value, checkedCategory)}
									/>
								</div>
							);
						})
						}
					</div>
					<div className={styles.column}>
						{sortedOrganisationCategory.filter((category, index) => {
							return index >= Math.ceil(sortedOrganisationCategory.length / 2);
						}).map(({ label, value }) => {
							const checkedCategory = categoryIds.includes(value);

							return (
								<div
									key={value}
									className={styles.categoryItem}
								>
									<Checkbox
										checked={checkedCategory}
										label={label}
										name={value}
										onChange={onCategoryChange(value, checkedCategory)}
									/>
								</div>
							);
						})
						}
					</div>
				</div>
			</div>
			{!isEditMode
				&& <div className={styles.buttons}>
					<Button
						onClick={onClickBackHandler}
						text='Back'
						withoutBackground
					/>
					<Tooltip
						content={blockTooltipText}
						isDisabled={!!types?.length}
						placement='top'
					>
						<div>
							<Button
								disabled={!types?.length || isOrganisationTypeTabError}
								onClick={switchToNext}
								text='Next'
							/>
						</div>
					</Tooltip>
				</div>
			}
		</>
	);
};

export default OrganisationType;
